if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', class ProductRecommendations extends HTMLElement {
    #intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#loadRecommendations();
      },
      { rootMargin: '0px 0px 600px 0px' }
    );

    #mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.target !== this || mutation.type !== 'attributes') continue;
        if (mutation.attributeName === 'data-error') continue;
        if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
        if (
          mutation.attributeName === 'data-recommendations-performed' &&
                    this.dataset.recommendationsPerformed === 'true'
        )
          continue;

        this.#loadRecommendations();
        break;
      }
    });

    #cachedRecommendations = {};
    #activeFetch = null;
    #staticContentHydrated = false;

    connectedCallback() {
      this.#mutationObserver.observe(this, { attributes: true });

      // Some recommendation sections are already rendered by Liquid. They do
      // not go through #renderRecommendations(), so their nested slider still
      // needs its interactive behaviour to be initialised here.
      if (this.dataset.recommendationsPerformed === 'true') {
        if (!this.#staticContentHydrated) {
          this.#staticContentHydrated = true;
          this.#hydrateDynamicContent();
        }
        return;
      }

      this.#intersectionObserver.observe(this);
    }

    #loadRecommendations() {
      const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
      const id = this.id;

      if (!productId || !id) {
      }

      if (recommendationsPerformed === 'true') return;

      this.#fetchCachedRecommendations(productId, sectionId, intent)
        .then((result) => {
          if (!result.success) {
            if (!Shopify.designMode) {
              this.#handleError(new Error(`Server returned ${result.status}`));
            }
            return;
          }

          const recommendations = this.#getRecommendations(result.data, id);
          if (recommendations?.innerHTML && recommendations.querySelector('.product-card')) {
            this.#renderRecommendations(recommendations);
            return;
          }

          const fallbackIntent = this.dataset.fallbackIntent;
          if (fallbackIntent && fallbackIntent !== intent) {
            this.#fetchCachedRecommendations(productId, sectionId, fallbackIntent)
              .then((fallbackResult) => {
                if (!fallbackResult.success) {
                  this.#handleError(new Error(`Server returned ${fallbackResult.status}`));
                  return;
                }

                const fallbackRecommendations = this.#getRecommendations(fallbackResult.data, id);
                if (fallbackRecommendations?.innerHTML && fallbackRecommendations.querySelector('.product-card')) {
                  this.#renderRecommendations(fallbackRecommendations);
                  return;
                }

                this.#handleError(new Error('No recommendations available'));
              })
              .catch((error) => this.#handleError(error));
            return;
          }

          this.#handleError(new Error('No recommendations available'));
        })
        .catch((e) => {
          this.#handleError(e);
        });
    }

    #getRecommendations(data, id) {
      const html = document.createElement('div');
      html.innerHTML = data || '';
      return html.querySelector(`product-recommendations[id="${id}"]`);
    }

    #renderRecommendations(recommendations) {
      this.dataset.recommendationsPerformed = 'true';
      this.innerHTML = recommendations.innerHTML;
      this.classList.add('product-recommendations--loaded');
      this.#hydrateDynamicContent();
    }

    async #fetchCachedRecommendations(productId, sectionId, intent) {
      const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

      const cachedResponse = this.#cachedRecommendations[url];
      if (cachedResponse) {
        return { success: true, data: cachedResponse };
      }

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      try {
        const response = await fetch(url, { signal: this.#activeFetch.signal });
        if (!response.ok) {
          return { success: false, status: response.status };
        }

        const text = await response.text();
        this.#cachedRecommendations[url] = text;
        return { success: true, data: text };
      } finally {
        this.#activeFetch = null;
      }
    }

    async #hydrateDynamicContent() {
      this.dispatchEvent(
        new CustomEvent('lazy-section-hydrated', {
          bubbles: true
        })
      );

      const waiters = [];
      if (this.querySelector('loop-slider') && !customElements.get('loop-slider')) {
        waiters.push(this.#waitForCustomElement('loop-slider'));
      }
      if (this.querySelector('slider-component') && !customElements.get('slider-component')) {
        waiters.push(this.#waitForCustomElement('slider-component'));
      }

      if (waiters.length) {
        await Promise.all(waiters);
      }

      requestAnimationFrame(() => {
        this.#reinitSliders();
      });
    }

    #waitForCustomElement(name, timeout = 2500) {
      return Promise.race([
        customElements.whenDefined(name),
        new Promise((resolve) => setTimeout(resolve, timeout))
      ]);
    }

    #reinitSliders() {
      this.querySelectorAll('slider-component').forEach((slider) => {
        // Server-rendered recommendations never pass through the slider's
        // deferred visibility callback. Initialise it before refreshing its
        // measurements so navigation buttons and drag controls are bound.
        if (!slider.isInitialized && typeof slider.handleDeferredInit === 'function') {
          slider.handleDeferredInit();
        }
        slider.reInit?.();
      });

      this.querySelectorAll('loop-slider').forEach((slider) => {
        if (slider.classList.contains('loop-slider--initialized')) return;
        if (typeof slider.connectedCallback === 'function') {
          slider.connectedCallback();
        }
      });
    }

    #handleError(error) {
      console.error('Product recommendations error:', error.message);
      this.classList.add('hidden');
      this.dataset.error = 'Error loading product recommendations';
    }
  });
}
